<template>
  <main class="explorer" :class="{ compact }">
    <ExplorerTopBar v-if="!showEdit" :is-login="isLogin" :display-name="displayName" @login="openLoginDialog" @logout="logout" @profile="openProfile" />

    <CommandHero
      v-if="showHomeRoute && !showEdit && !showProfile && !activeLevel"
      :archive-count="archiveLevels.length"
      :mine-count="mineLevels.length"
      :favorite-count="favoriteLevels.length"
      :visit-summary="visitSummary"
      @random="randomLevel"
      @refresh="refreshHome"
      @visit-stats="openVisitStats"
    />

    <VisitStatsPanel
      v-if="!showEdit && !showProfile && showVisitStats"
      :visit-summary="visitSummary"
      @back="closeVisitStats"
      @refresh="loadVisitDashboard"
    />

    <LevelReader
      v-if="!showEdit && !showProfile && !showVisitStats && activeLevel"
      :active-level="activeLevel"
      :active-source="activeSource"
      :source-text="sourceText"
      :messages="messages"
      :is-owner="isOwner"
      :patrol-expire-days="patrolExpireDays"
      v-model:message-content="messageContent"
      @leave="leaveLevel"
      @occupy="occupyLevel"
      @favorite="toggleFavorite"
      @guard="guardLevel"
      @patrol="patrolActiveLevel"
      @send="sendMessage"
    />

    <LevelLibrary
      v-if="showHomeRoute && !showEdit && !showProfile && !activeLevel"
      v-model:tab="tab"
      v-model:search-field="searchField"
      v-model:keyword="keyword"
      v-model:status-filter="statusFilter"
      :tab-title="tabTitle"
      :levels="currentLevels"
      :patrol-expire-days="patrolExpireDays"
      @load-archive="loadArchive"
      @open-mine="openMine"
      @open-favorites="openFavorites"
      @open-level="openLevel"
      @open-edit="openEdit"
      @patrol-level="patrolLevel"
    />

    <ProfileCenter
      v-if="!showEdit && showProfile"
      :display-name="displayName"
      @close="closeProfile"
    />

    <EditorStudio
      v-if="showEdit"
      :edit-form="editForm"
      :edit-saving="editSaving"
      :uploading-field="uploadingField"
      :edit-preview-style="editPreviewStyle"
      :edit-cover-style="editCoverStyle"
      :edit-cover-url="editCoverUrl"
      :patrol-expire-days="patrolExpireDays"
      @close="closeEdit"
      @save="saveLevel"
      @patrol="patrolEditingLevel"
      @image-file="handleImageFile"
    />

    <AuthDialog
      v-model="showLogin"
      v-model:auth-mode="authMode"
      :login-form="loginForm"
      :login-rules="loginRules"
      :loading="loginLoading"
      @submit="submitAuth"
    />
  </main>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import type { FormRules } from 'element-plus';
import { ajax, uploadImgBlob } from '@/api';
import { useAuthStore } from '@/store/auth';
import { userData } from '@/store/userData';
import ExplorerTopBar from './level-explorer/top-bar.vue';
import CommandHero from './level-explorer/command-hero.vue';
import LevelReader from './level-explorer/level-reader.vue';
import LevelLibrary from './level-explorer/level-library.vue';
import EditorStudio from './level-explorer/editor-studio.vue';
import AuthDialog from './level-explorer/auth-dialog.vue';
import ProfileCenter from './level-explorer/profile-center.vue';
import VisitStatsPanel from './level-explorer/visit-stats-panel.vue';
import type { ImageField, LevelItem, LevelSource, LevelTab, SearchField } from './level-explorer/types';

defineProps<{ compact?: boolean }>();

type VisitSummary = {
  visible: boolean;
  loading: boolean;
  todayVisitors: number;
  todayVisits: number;
  weekVisits: number;
  monthVisitors: number;
  monthVisits: number;
};

const tab = ref<LevelTab>('archive');
const archiveLevels = ref<LevelItem[]>([]);
const mineLevels = ref<LevelItem[]>([]);
const favoriteLevels = ref<LevelItem[]>([]);
const activeLevel = ref<LevelItem | null>(null);
const activeSource = ref<LevelSource>('archive');
const messages = ref<any[]>([]);
const messageContent = ref('');
const searchField = ref<SearchField>('name');
const keyword = ref('');
const statusFilter = ref<number | ''>('');
const showLogin = ref(false);
const showProfile = ref(false);
const patrolExpireDays = ref(5);
const loginLoading = ref(false);
const authMode = ref<'login' | 'register'>('login');
const authStore = useAuthStore();
const useData = userData();
const route = useRoute();
const router = useRouter();
const visitDashboardPath = '/visit/dashboard';
const homePath = '/home';
const randomPath = '/level/random';
const homeRoutes = new Set(['/home', '/pc/home', '/pad/home', '/phone/home', '/']);
const showHomeRoute = computed(() => homeRoutes.has(route.path));
const showVisitStats = computed(() => route.path === visitDashboardPath);
const showRandomLevel = computed(() => route.path === randomPath);
const routeLevelId = computed(() => {
  const id = route.params.id;
  const rawId = Array.isArray(id) ? id[0] : id;
  return Number(rawId || 0);
});
const showLevelEditRoute = computed(() => route.path.startsWith('/level/') && route.path.endsWith('/edit'));
const showLevelDetailRoute = computed(() => route.path.startsWith('/level/') && !showRandomLevel.value && !showLevelEditRoute.value);
const showEdit = ref(false);
const editSaving = ref(false);
const uploadingField = ref<'' | ImageField>('');
const backgroundPreviewUrl = ref('');
const coverPreviewUrl = ref('');
const visitSummary = reactive<VisitSummary>({
  visible: false,
  loading: false,
  todayVisitors: 0,
  todayVisits: 0,
  weekVisits: 0,
  monthVisitors: 0,
  monthVisits: 0
});
const editForm = reactive<Partial<LevelItem>>({
  id: 0,
  levelNum: '',
  name: '',
  title: '',
  img: '',
  backgroundImg: '',
  description: '',
  notice: '',
  html: '',
  status: 0,
  auditStatus: 0,
  rejectReason: '',
  guardedAt: '',
  occupiedAt: '',
  enterNum: 0,
  messageNum: 0,
  favoriteNum: 0
});
const loginForm = reactive({
  username: '',
  password: ''
});
const loginRules: FormRules = {
  username: [
    { required: true, message: '请输入账号', trigger: 'blur' },
    { pattern: /^[a-zA-Z0-9]{10}$/, message: '账号只能是10位字母或数字', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 8, max: 20, message: '密码长度必须为8-20位', trigger: 'blur' },
    { pattern: /^[a-zA-Z0-9.]+$/, message: '密码只能包含字母、数字和.', trigger: 'blur' }
  ]
};

const isLogin = computed(() => !!authStore.loginKey || !!sessionStorage.getItem('loginKey'));
const displayName = computed(() => useData.name || sessionStorage.getItem('name') || '探索者');
const sourceText = computed(() => {
  if (activeSource.value === 'random') return 'TRANSIT MODE';
  if (activeSource.value === 'mine') return 'MY LAYER';
  if (activeSource.value === 'favorite') return 'SAVED FILE';
  return 'ARCHIVE MODE';
});
const tabTitle = computed(() => {
  if (tab.value === 'mine') return '我的占领层级';
  if (tab.value === 'favorite') return '收藏索引';
  return '公开层级档案';
});

const currentLevels = computed(() => {
  if (tab.value === 'mine') return mineLevels.value;
  if (tab.value === 'favorite') return favoriteLevels.value;
  return archiveLevels.value;
});

const isOwner = computed(() => {
  const userId = Number(sessionStorage.getItem('userId') || 0);
  const name = sessionStorage.getItem('name') || '';
  return !!activeLevel.value && (activeLevel.value.ownerUserId === userId || (!!name && activeLevel.value.ownerName === name));
});

const editBackgroundUrl = computed(() => backgroundPreviewUrl.value || editForm.backgroundImg || editForm.img || coverPreviewUrl.value || '');
const editCoverUrl = computed(() => coverPreviewUrl.value || editForm.img || '');
const editPreviewStyle = computed(() => {
  return editBackgroundUrl.value ? { backgroundImage: `url(${editBackgroundUrl.value})` } : {};
});
const editCoverStyle = computed(() => {
  return editCoverUrl.value ? { backgroundImage: `url(${editCoverUrl.value})` } : {};
});

const openLoginDialog = () => {
  authMode.value = 'login';
  showLogin.value = true;
};

const requireLogin = () => {
  if (!sessionStorage.getItem('loginKey')) {
    openLoginDialog();
    ElMessage.warning('请先登录后再操作');
    return false;
  }
  return true;
};

const submitAuth = () => {
  loginLoading.value = true;
  if (authMode.value === 'register') {
    register();
    return;
  }
  login();
};

const login = () => {
  ajax({
    url: 'user/login',
    method: 'POST',
    data: {
      username: loginForm.username,
      password: loginForm.password
    },
    success: (res: any) => {
      loginLoading.value = false;
      if (res.code !== 0) {
        ElMessage.error(res.msg);
        return;
      }
      const data = res.data || {};
      const token = data.token || data.loginKey || '';
      const name = data.name || data.username || loginForm.username;
      authStore.setLoginKey(token);
      useData.setLoginKey(token);
      useData.setName(name);
      if (data.imgHead) useData.setImgHead(data.imgHead);
      if (data.imgDecoration) useData.setImgDecoration(data.imgDecoration);
      sessionStorage.setItem('userId', String(data.id || ''));
      sessionStorage.setItem('role', data.role || 'user');
      showLogin.value = false;
      loginForm.password = '';
      loadVisitDashboard();
      ElMessage.success(res.msg || '登录成功');
    },
    error: () => {
      loginLoading.value = false;
      ElMessage.error('登录失败，请稍后重试');
    }
  });
};

const register = () => {
  ajax({
    url: 'user/register',
    method: 'POST',
    data: {
      username: loginForm.username,
      password: loginForm.password
    },
    success: (res: any) => {
      if (res.code !== 0) {
        loginLoading.value = false;
        ElMessage.error(res.msg);
        return;
      }
      ElMessage.success('注册成功，正在登录');
      login();
    },
    error: () => {
      loginLoading.value = false;
      ElMessage.error('注册失败，请稍后重试');
    }
  });
};

const logout = () => {
  authStore.removeLoginKey(false);
  useData.removeAll();
  sessionStorage.removeItem('userId');
  sessionStorage.removeItem('role');
  authMode.value = 'login';
  showLogin.value = true;
  mineLevels.value = [];
  favoriteLevels.value = [];
  showProfile.value = false;
  loadVisitDashboard();
  ElMessage.success('已退出登录');
};

const loadArchive = () => {
  const where: any[] = [];
  if (keyword.value) {
    where.push({ name: searchField.value, type: 'like', value: keyword.value });
  }
  if (statusFilter.value !== '') {
    where.push({ name: 'status', type: 'eq', value: statusFilter.value });
  }
  where.push({ name: 'auditStatus', type: 'in', value: [0, 1, 2] });
  ajax({
    url: 'level/list',
    method: 'POST',
    data: { paged: false, where, orderBy: 'id DESC' },
    isLoading: false,
    success: (res: any) => {
      if (res.code === 0) archiveLevels.value = res.dataList || [];
      else ElMessage.error(res.msg);
    }
  });
};

const numberValue = (value: unknown) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const resetVisitSummary = () => {
  visitSummary.visible = false;
  visitSummary.loading = false;
  visitSummary.todayVisitors = 0;
  visitSummary.todayVisits = 0;
  visitSummary.weekVisits = 0;
  visitSummary.monthVisitors = 0;
  visitSummary.monthVisits = 0;
};

const loadVisitDashboard = () => {
  visitSummary.loading = true;
  visitSummary.visible = true;
  ajax({
    url: 'visit/dashboard',
    method: 'POST',
    data: {},
    isLoading: false,
    success: (res: any) => {
      visitSummary.loading = false;
      if (res.code !== 0) {
        visitSummary.visible = false;
        ElMessage.warning(res.msg || '访客统计读取失败');
        return;
      }
      const summary = res.data?.summary || {};
      visitSummary.visible = true;
      visitSummary.todayVisitors = numberValue(summary.todayVisitors);
      visitSummary.todayVisits = numberValue(summary.todayVisits);
      visitSummary.weekVisits = numberValue(summary.weekVisits);
      visitSummary.monthVisitors = numberValue(summary.monthVisitors);
      visitSummary.monthVisits = numberValue(summary.monthVisits);
    },
    error: () => {
      resetVisitSummary();
    }
  });
};

const refreshHome = () => {
  loadArchive();
  loadVisitDashboard();
};

const leaveVisitStatsRoute = () => {
  if (showVisitStats.value) {
    router.push(homePath);
  }
};

const goHome = () => {
  if (route.path !== homePath) {
    router.push(homePath);
  }
};

const loadRandomLevel = () => {
  activeLevel.value = null;
  messages.value = [];
  activeSource.value = 'random';
  ajax({
    url: 'level/random',
    method: 'GET',
    success: (res: any) => {
      if (res.code === 0) {
        showProfile.value = false;
        leaveVisitStatsRoute();
        showEdit.value = false;
        activeLevel.value = res.data;
        activeSource.value = 'random';
        loadMessages();
      } else {
        ElMessage.warning(res.msg);
      }
    }
  });
};

const randomLevel = () => {
  showProfile.value = false;
  showEdit.value = false;
  if (showRandomLevel.value) {
    loadRandomLevel();
    return;
  }
  router.push(randomPath);
};

const openProfile = () => {
  if (!requireLogin()) return;
  activeLevel.value = null;
  leaveVisitStatsRoute();
  showProfile.value = true;
};

const closeProfile = () => {
  showProfile.value = false;
};

const openVisitStats = () => {
  activeLevel.value = null;
  showProfile.value = false;
  showEdit.value = false;
  router.push(visitDashboardPath);
  loadVisitDashboard();
};

const closeVisitStats = () => {
  goHome();
};

const openLevel = (level: LevelItem) => {
  if (Number(level.auditStatus) === 1 && tab.value !== 'mine') {
    ElMessage.warning(getBlockedLevelReason(level));
    return;
  }
  router.push({
    path: `/level/${level.id}`,
    query: { source: tab.value }
  });
};

const loadLevelDetail = (level: Partial<LevelItem> & { id: number }, onSuccess: (detail: LevelItem) => void) => {
  ajax({
    url: `level/detail/${level.id}`,
    method: 'GET',
    success: (res: any) => {
      if (res.code === 0) {
        onSuccess(res.data || level);
      } else {
        ElMessage.warning(res.msg || getBlockedLevelReason(level));
      }
    },
    error: () => {
      ElMessage.error('层级资料读取失败，请稍后重试');
    }
  });
};

const getBlockedLevelReason = (level: Partial<LevelItem>) => {
  if (Number(level.auditStatus) === 1) return '该层级资料正在管理员审核中，暂时不能查看。';
  if (Number(level.auditStatus) === 3) return `该层级资料审核未通过，暂时不能查看。${level.rejectReason ? `原因：${level.rejectReason}` : ''}`;
  if (Number(level.status) === -1) return '该层级已被删除，不能查看。';
  return '当前层级暂时不能查看。';
};

const leaveLevel = () => {
  activeLevel.value = null;
  messages.value = [];
  activeSource.value = 'archive';
  goHome();
};

const randomAccessPayload = () => {
  const randomToken = activeSource.value === 'random' ? activeLevel.value?.randomToken : '';
  if (!randomToken) {
    ElMessage.warning('请先通过随机探索进入层级');
    return null;
  }
  return { randomToken };
};

const occupyLevel = () => {
  if (!activeLevel.value || !requireLogin()) return;
  const payload = randomAccessPayload();
  if (!payload) return;
  ajax({
    url: 'level/random/occupy',
    method: 'POST',
    data: payload,
    success: handleLevelChange
  });
};

const guardLevel = () => {
  if (!activeLevel.value || !requireLogin()) return;
  const payload = randomAccessPayload();
  if (!payload) return;
  ajax({
    url: 'level/random/guard',
    method: 'POST',
    data: payload,
    success: handleLevelChange
  });
};

const toggleFavorite = () => {
  if (!activeLevel.value || !requireLogin()) return;
  const payload = randomAccessPayload();
  if (!payload) return;
  ajax({
    url: 'level/random/favorite/toggle',
    method: 'POST',
    data: payload,
    success: (res: any) => {
      if (res.code === 0) ElMessage.success(res.msg);
      else ElMessage.error(res.msg);
    }
  });
};

const handleLevelChange = (res: any) => {
  if (res.code === 0) {
    const randomToken = res.data?.randomToken || activeLevel.value?.randomToken || '';
    activeLevel.value = { ...res.data, randomToken };
    ElMessage.success(res.msg);
    loadArchive();
  } else {
    ElMessage.error(res.msg);
  }
};

const loadMessages = () => {
  if (!activeLevel.value) return;
  const payload = randomAccessPayload();
  if (!payload) return;
  ajax({
    url: 'level/random/message/list',
    method: 'POST',
    data: payload,
    isLoading: false,
    success: (res: any) => {
      if (res.code === 0) messages.value = res.dataList || [];
    }
  });
};

const sendMessage = () => {
  if (!activeLevel.value || !requireLogin()) return;
  if (!messageContent.value.trim()) {
    ElMessage.warning('请输入留言内容');
    return;
  }
  const payload = randomAccessPayload();
  if (!payload) return;
  ajax({
    url: 'level/random/message/add',
    method: 'POST',
    data: { ...payload, content: messageContent.value.trim() },
    success: (res: any) => {
      if (res.code === 0) {
        messageContent.value = '';
        loadMessages();
        ElMessage.success(res.msg);
      } else {
        ElMessage.error(res.msg);
      }
    }
  });
};

const openMine = () => {
  tab.value = 'mine';
  if (!requireLogin()) return;
  leaveVisitStatsRoute();
  ajax({
    url: 'level/mine',
    method: 'POST',
    success: (res: any) => {
      if (res.code === 0) mineLevels.value = res.dataList || [];
      else ElMessage.error(res.msg);
    }
  });
};

const openFavorites = () => {
  tab.value = 'favorite';
  if (!requireLogin()) return;
  leaveVisitStatsRoute();
  ajax({
    url: 'level/favorite/list',
    method: 'POST',
    success: (res: any) => {
      if (res.code === 0) favoriteLevels.value = res.dataList || [];
      else ElMessage.error(res.msg);
    }
  });
};

const openEdit = (level: LevelItem) => {
  if (!requireLogin()) return;
  router.push(`/level/${level.id}/edit`);
};

const openEditWithDetail = (level: LevelItem) => {
  revokePreviewUrls();
  leaveVisitStatsRoute();
  Object.assign(editForm, {
    id: level.id,
    levelNum: level.levelNum || '',
    name: level.name || '',
    title: level.title || '',
    img: level.img || '',
    backgroundImg: level.backgroundImg || '',
    description: level.description || '',
    notice: level.notice || '',
    html: level.html || '',
    status: level.status || 0,
    auditStatus: level.auditStatus || 0,
    rejectReason: level.rejectReason || '',
    guardedAt: level.guardedAt || '',
    occupiedAt: level.occupiedAt || '',
    enterNum: level.enterNum || 0,
    messageNum: level.messageNum || 0,
    favoriteNum: level.favoriteNum || 0
  });
  showEdit.value = true;
};

const closeEdit = () => {
  showEdit.value = false;
  revokePreviewUrls();
  goHome();
  openMine();
};

const revokePreviewUrls = () => {
  if (backgroundPreviewUrl.value) URL.revokeObjectURL(backgroundPreviewUrl.value);
  if (coverPreviewUrl.value) URL.revokeObjectURL(coverPreviewUrl.value);
  backgroundPreviewUrl.value = '';
  coverPreviewUrl.value = '';
};

const handleImageFile = (field: ImageField, file: File) => {
  if (!requireLogin()) return;
  if (!file.type.startsWith('image/')) {
    ElMessage.warning('请选择图片文件');
    return;
  }
  if (file.size > 8 * 1024 * 1024) {
    ElMessage.warning('图片不能超过 8MB');
    return;
  }

  const localUrl = URL.createObjectURL(file);
  if (field === 'backgroundImg') {
    if (backgroundPreviewUrl.value) URL.revokeObjectURL(backgroundPreviewUrl.value);
    backgroundPreviewUrl.value = localUrl;
  } else {
    if (coverPreviewUrl.value) URL.revokeObjectURL(coverPreviewUrl.value);
    coverPreviewUrl.value = localUrl;
  }

  uploadingField.value = field;
  uploadImgBlob({
    url: 'file/uploadImg',
    file,
    data: { fileName: file.name },
    loading: { open: false, text: '' },
    success: (res: any) => {
      uploadingField.value = '';
      if (res.code !== 0) {
        ElMessage.error(res.msg || '图片上传失败');
        return;
      }
      const uploadedUrl = res.data?.url || '';
      if (!uploadedUrl) {
        ElMessage.warning('图片已选择，但未获取到上传地址');
        return;
      }
      editForm[field] = uploadedUrl;
      ElMessage.success(field === 'backgroundImg' ? '背景图已上传' : '封面图已上传');
    },
    error: () => {
      uploadingField.value = '';
      ElMessage.error('图片上传失败，请稍后重试');
    }
  });
};

const saveLevel = () => {
  if (!editForm.id || !requireLogin()) return;
  if (uploadingField.value) {
    ElMessage.warning('图片还在上传，请稍后保存');
    return;
  }
  editSaving.value = true;
  const payload = { ...editForm };
  delete payload.levelNum;
  delete payload.ownerUserId;
  delete payload.ownerName;
  delete payload.occupiedAt;
  delete payload.guardedAt;
  delete payload.enterNum;
  delete payload.messageNum;
  delete payload.favoriteNum;
  delete payload.status;
  delete payload.auditStatus;
  delete payload.rejectReason;
  ajax({
    url: 'level/update',
    method: 'POST',
    data: payload,
    success: (res: any) => {
      editSaving.value = false;
      if (res.code === 0) {
        ElMessage.success('层级信息已提交，等待管理员审核');
        closeEdit();
      } else {
        ElMessage.error(res.msg);
      }
    },
    error: () => {
      editSaving.value = false;
      ElMessage.error('保存失败，请稍后重试');
    }
  });
};

const loadPublicSettings = () => {
  ajax({
    url: 'system-setting/public',
    method: 'GET',
    isLoading: false,
    success: (res: any) => {
      if (res.code === 0 && res.data?.patrolExpireDays) {
        patrolExpireDays.value = Number(res.data.patrolExpireDays) || 5;
      }
    }
  });
};

const patchLevelInLists = (level: LevelItem) => {
  const patch = (list: LevelItem[]) => {
    const index = list.findIndex((item) => item.id === level.id);
    if (index >= 0) list[index] = { ...list[index], ...level };
  };
  patch(archiveLevels.value);
  patch(mineLevels.value);
  patch(favoriteLevels.value);
};

const patrolLevel = (level: LevelItem) => {
  if (!requireLogin()) return;
  ajax({
    url: `level/guard/${level.id}`,
    method: 'POST',
    success: (res: any) => {
      if (res.code === 0) {
        const data = res.data || level;
        patchLevelInLists(data);
        if (activeLevel.value?.id === data.id) activeLevel.value = { ...activeLevel.value, ...data };
        if (editForm.id === data.id) {
          editForm.guardedAt = data.guardedAt || editForm.guardedAt;
        }
        ElMessage.success(res.msg || '巡逻成功');
      } else {
        ElMessage.error(res.msg);
      }
    }
  });
};

const patrolActiveLevel = () => {
  if (activeLevel.value) patrolLevel(activeLevel.value);
};

const patrolEditingLevel = () => {
  if (!editForm.id) return;
  patrolLevel(editForm as LevelItem);
};

const getRouteSource = (): LevelSource => {
  const source = String(route.query.source || 'archive');
  if (source === 'mine' || source === 'favorite' || source === 'random') return source;
  return 'archive';
};

const clearActiveLevel = () => {
  activeLevel.value = null;
  messages.value = [];
  activeSource.value = 'archive';
};

const syncRouteView = () => {
  if (showVisitStats.value) {
    clearActiveLevel();
    showProfile.value = false;
    showEdit.value = false;
    loadVisitDashboard();
    return;
  }

  if (showRandomLevel.value) {
    showProfile.value = false;
    showEdit.value = false;
    loadRandomLevel();
    return;
  }

  if (showLevelEditRoute.value) {
    if (!routeLevelId.value) {
      goHome();
      return;
    }
    if (!requireLogin()) {
      goHome();
      return;
    }
    showProfile.value = false;
    loadLevelDetail({ id: routeLevelId.value }, openEditWithDetail);
    return;
  }

  if (showLevelDetailRoute.value) {
    if (!routeLevelId.value) {
      goHome();
      return;
    }
    showProfile.value = false;
    showEdit.value = false;
    loadLevelDetail({ id: routeLevelId.value }, (detail) => {
      activeLevel.value = detail;
      activeSource.value = getRouteSource();
      messages.value = [];
    });
    return;
  }

  if (showHomeRoute.value) {
    showEdit.value = false;
    clearActiveLevel();
  }
};

onMounted(() => {
  loadPublicSettings();
  loadArchive();
  loadVisitDashboard();
  syncRouteView();
});
watch(
  () => route.fullPath,
  () => syncRouteView()
);
onBeforeUnmount(() => {
  revokePreviewUrls();
});
</script>

<style lang="scss">
.explorer {
  --bg: var(--cyber-bg, #0b101e);
  --surface: rgba(11, 16, 30, 0.74);
  --surface-2: rgba(15, 23, 42, 0.86);
  --line: rgba(0, 240, 216, 0.22);
  --line-strong: rgba(0, 240, 216, 0.48);
  --text: var(--cyber-text, #e8edf7);
  --muted: var(--cyber-muted, #8992a9);
  --soft: rgba(137, 146, 169, 0.58);
  --accent: var(--cyber-cyan, #00f0d8);
  --accent-2: var(--cyber-purple, #9d5cff);
  --accent-ink: #06101d;
  --blue: var(--cyber-blue, #22b8ff);
  min-height: 100vh;
  color: var(--text);
  font-family: var(--cyber-font, "Inter", "PingFang SC", "Microsoft YaHei", Arial, sans-serif);
  background:
    linear-gradient(rgba(0, 240, 216, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(157, 92, 255, 0.032) 1px, transparent 1px),
    radial-gradient(circle at 18% 6%, rgba(34, 184, 255, 0.12), transparent 34%),
    radial-gradient(circle at 86% 8%, rgba(157, 92, 255, 0.1), transparent 30%),
    linear-gradient(180deg, #0f172a, #0b101e 48%, #070b14);
  background-size: 72px 72px, 72px 72px, auto, auto, auto;
  padding: 22px;
  overflow-x: hidden;
}

.topbar,
.command,
.workspace,
.level-brief,
.action-panel {
  border: 1px solid var(--line);
  background:
    linear-gradient(180deg, rgba(0, 240, 216, 0.05), rgba(157, 92, 255, 0.024)),
    var(--surface);
  backdrop-filter: blur(26px) saturate(125%);
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.38),
    0 0 26px rgba(0, 240, 216, 0.055),
    inset 0 1px 0 rgba(0, 240, 216, 0.1);
  border-radius: 8px;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  max-width: 1320px;
  margin: 0 auto 18px;
  padding: 12px 14px 12px 12px;
}

.identity,
.top-actions,
.command-actions,
.panel-actions,
.filters,
.tabs,
.edit-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.identity {
  gap: 12px;
}

.identity strong {
  display: block;
  letter-spacing: 0;
  font-size: 15px;
}

.identity span,
.eyebrow,
.operator,
.brief-heading small,
.card-body small {
  color: var(--muted);
  font-size: 12px;
  letter-spacing: 0;
  text-transform: uppercase;
}

.sigil {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(0, 240, 216, 0.44);
  background: rgba(0, 240, 216, 0.09);
  color: var(--accent);
  font-weight: 800;
  border-radius: 8px;
  box-shadow: 0 0 22px rgba(0, 240, 216, 0.15);
}

.operator {
  padding: 9px 12px;
  border: 1px solid var(--line);
  background: rgba(0, 240, 216, 0.055);
  border-radius: 8px;
}


.top-actions .login-entry-btn {
  --el-button-bg-color: rgba(157, 92, 255, 0.12);
  --el-button-border-color: rgba(157, 92, 255, 0.46);
  --el-button-text-color: #c7a8ff;
  --el-button-hover-bg-color: rgba(157, 92, 255, 0.2);
  --el-button-hover-border-color: rgba(157, 92, 255, 0.72);
  --el-button-hover-text-color: #eadcff;
  box-shadow: 0 0 22px rgba(157, 92, 255, 0.1);
}

.command-actions .refresh-archive-btn {
  --el-button-bg-color: rgba(34, 184, 255, 0.1);
  --el-button-border-color: rgba(34, 184, 255, 0.42);
  --el-button-text-color: var(--blue);
  --el-button-hover-bg-color: rgba(34, 184, 255, 0.17);
  --el-button-hover-border-color: rgba(34, 184, 255, 0.7);
  --el-button-hover-text-color: #bfeaff;
  box-shadow: 0 0 22px rgba(34, 184, 255, 0.09);
}

.command {
  max-width: 1320px;
  min-height: 420px;
  margin: 0 auto 18px;
  padding: 42px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 34px;
  align-items: end;
  position: relative;
  overflow: hidden;
}

.command::after {
  content: "";
  position: absolute;
  width: 42%;
  min-width: 300px;
  height: 100%;
  right: 0;
  top: 0;
  border-left: 1px solid rgba(0, 240, 216, 0.12);
  background:
    repeating-linear-gradient(90deg, rgba(0, 240, 216, 0.08) 0 1px, transparent 1px 18px),
    repeating-linear-gradient(0deg, rgba(157, 92, 255, 0.055) 0 1px, transparent 1px 24px),
    linear-gradient(90deg, transparent, rgba(0, 240, 216, 0.055));
  opacity: 0.82;
  pointer-events: none;
}

.command-copy {
  position: relative;
  z-index: 1;
  max-width: 780px;
}

.eyebrow {
  color: var(--blue);
  font-weight: 650;
}

h1,
h2,
h3,
h4,
p {
  margin: 0;
}

h1 {
  margin-top: 14px;
  font-size: clamp(42px, 6vw, 80px);
  line-height: 0.96;
  letter-spacing: 0;
  max-width: 820px;
}

.command-copy p {
  max-width: 620px;
  margin: 22px 0 28px;
  color: var(--muted);
  font-size: 16px;
  line-height: 1.72;
}

.command-guide-link {
  display: inline-flex;
  align-items: center;
  margin: -10px 0 26px;
  color: var(--accent);
  font-weight: 700;
  line-height: 1.7;
  text-decoration: underline;
  text-underline-offset: 5px;
  text-decoration-color: rgba(0, 240, 216, 0.45);
  transition: color 0.25s ease, text-decoration-color 0.25s ease, text-shadow 0.25s ease;
}

.command-guide-link:hover {
  color: #cffff8;
  text-decoration-color: rgba(0, 240, 216, 0.86);
  text-shadow: 0 0 14px rgba(0, 240, 216, 0.24);
}

.status-board {
  position: relative;
  z-index: 1;
  border: 1px solid var(--line);
  background: rgba(5, 10, 22, 0.46);
  padding: 18px;
  border-radius: 8px;
}

.board-row {
  display: flex;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid rgba(0, 240, 216, 0.12);
}

.board-row-action {
  width: 100%;
  color: inherit;
  font: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgba(0, 240, 216, 0.12);
  cursor: pointer;
  transition: background-color 0.25s ease, padding 0.25s ease;
}

.board-row-action:hover {
  padding-inline: 10px;
  background: rgba(0, 240, 216, 0.06);
}

.board-row-action:focus-visible {
  outline: 1px solid rgba(0, 240, 216, 0.7);
  outline-offset: 4px;
}

.board-row span,
.board-note {
  color: var(--muted);
}

.board-row strong {
  color: var(--accent);
  font-size: 24px;
  letter-spacing: 0;
  text-shadow: 0 0 18px rgba(0, 240, 216, 0.22);
}

.visit-board-row strong {
  color: var(--blue);
}

.board-note {
  margin-top: 14px;
  line-height: 1.6;
  font-size: 12px;
}

.level-reader {
  position: relative;
  min-height: 76vh;
  margin: 0 -22px 18px;
  background-position: center;
  background-size: cover;
}

.reader-shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(5, 9, 20, 0.96), rgba(11, 16, 30, 0.68)),
    linear-gradient(180deg, rgba(11, 16, 30, 0.25), rgba(5, 9, 20, 0.94));
}

.reader-layout {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 390px;
  gap: 20px;
  max-width: 1320px;
  min-height: 76vh;
  margin: 0 auto;
  padding: 38px 22px;
  align-items: end;
}

.reader-layout.readonly-layout {
  grid-template-columns: minmax(0, 1fr);
  max-width: 1180px;
  align-items: stretch;
}

.readonly-layout .level-brief {
  max-width: none;
}

.readonly-banner {
  padding: 22px 24px;
  border: 1px solid rgba(0, 240, 216, 0.24);
  background:
    linear-gradient(135deg, rgba(0, 240, 216, 0.075), rgba(157, 92, 255, 0.035)),
    rgba(5, 10, 22, 0.68);
  border-radius: 8px;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.34), 0 0 22px rgba(0, 240, 216, 0.08);
  backdrop-filter: blur(20px) saturate(125%);
}

.readonly-banner .section-title {
  margin: 0 0 8px;
}

.readonly-banner p {
  max-width: 820px;
  color: var(--muted);
  line-height: 1.8;
}

.level-brief,
.action-panel {
  padding: 24px;
}

.level-brief {
  max-width: 820px;
}

.text-button {
  color: var(--accent);
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  margin-bottom: 24px;
  transition: color 0.28s ease, text-shadow 0.28s ease;
}

.text-button:hover {
  color: #cffff8;
  text-shadow: 0 0 12px rgba(0, 240, 216, 0.24);
}

.reader-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  margin-bottom: 26px;
  padding: 0 16px;
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.42);
  background: rgba(0, 240, 216, 0.09);
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 0 22px rgba(0, 240, 216, 0.11);
  transition: border-color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
}

.reader-back::before {
  content: "<";
  font-weight: 700;
}

.reader-back:hover {
  border-color: rgba(0, 240, 216, 0.68);
  background: rgba(0, 240, 216, 0.14);
  box-shadow: 0 0 28px rgba(0, 240, 216, 0.16);
  transform: translateY(-1px);
}

.brief-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.brief-heading span {
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.36);
  background: rgba(0, 240, 216, 0.08);
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
}

.level-brief h2 {
  margin: 16px 0;
  font-size: clamp(38px, 5vw, 64px);
  line-height: 0.98;
  letter-spacing: 0;
}

.level-brief p {
  color: var(--muted);
  line-height: 1.8;
}

.reader-cover {
  width: min(560px, 100%);
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  margin-top: 22px;
  padding: 12px;
  border: 1px solid rgba(0, 240, 216, 0.18);
  background: rgba(5, 10, 22, 0.48);
  border-radius: 8px;
  backdrop-filter: blur(16px);
}

.reader-cover-media {
  height: 116px;
  overflow: hidden;
  border: 1px solid rgba(0, 240, 216, 0.2);
  border-radius: 8px;
  background: #0b101e;
}

.reader-cover-media img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.reader-cover strong {
  display: block;
  margin-top: 8px;
  color: var(--text);
  line-height: 1.35;
}

.metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(118px, 1fr));
  gap: 10px;
  margin: 24px 0;
}

.metrics div {
  border: 1px solid rgba(0, 240, 216, 0.16);
  background: rgba(0, 240, 216, 0.045);
  padding: 13px;
  border-radius: 8px;
}

.metrics span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.metrics strong {
  display: block;
  margin-top: 7px;
  color: var(--accent);
  letter-spacing: 0;
}

.metric-owner {
  word-break: break-word;
}

.metric-owner.vacant {
  color: #94a3b8;
}

.notice,
.level-html {
  border-left: 2px solid var(--blue);
  background: rgba(34, 184, 255, 0.055);
  padding: 14px 16px;
  border-radius: 0 8px 8px 0;
}

.level-html {
  margin-top: 16px;
  overflow: visible;
  line-height: 1.75;
}

.level-html img {
  display: inline-block;
  max-width: 100%;
  height: auto;
  vertical-align: middle;
}

.level-html .w-e-image-container {
  display: inline-block;
  max-width: 100%;
}

.level-html .w-e-image-container img {
  width: 100%;
  height: 100%;
}

.level-html table {
  max-width: 100%;
  margin: 14px 0;
  border-collapse: collapse !important;
  border: 1px solid rgba(0, 240, 216, 0.34) !important;
  color: var(--text);
  background: rgba(5, 10, 22, 0.36);
}

.level-html th,
.level-html td {
  min-width: 44px;
  min-height: 32px;
  padding: 8px 10px;
  border: 1px solid rgba(0, 240, 216, 0.34) !important;
  line-height: 1.55;
  text-align: left;
  vertical-align: top;
}

.level-html th {
  color: #dffefa;
  font-weight: 800;
  background: rgba(0, 240, 216, 0.12);
}

.level-html td:empty::before,
.level-html th:empty::before {
  content: "\00a0";
}

.patrol-strip,
.audit-tip,
.editor-help {
  margin: 16px 0;
  padding: 14px 16px;
  border: 1px solid rgba(0, 240, 216, 0.18);
  border-left: 2px solid var(--accent);
  color: var(--text);
  background: rgba(0, 240, 216, 0.06);
  border-radius: 0 8px 8px 0;
}

.patrol-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.patrol-strip span,
.editor-help {
  color: var(--muted);
  line-height: 1.7;
}

.audit-tip {
  border-left-color: #f5c542;
  background: rgba(245, 197, 66, 0.08);
}

.audit-tip.reject {
  border-left-color: #ff6b7a;
  background: rgba(255, 107, 122, 0.08);
}

.section-title {
  color: var(--text);
}

.panel-actions {
  align-items: stretch;
}

.panel-actions .el-button {
  flex: 1;
  margin-left: 0;
  min-height: 42px;
  --el-button-bg-color: rgba(8, 13, 26, 0.72);
  --el-button-border-color: rgba(0, 240, 216, 0.3);
  --el-button-text-color: var(--muted);
  --el-button-hover-bg-color: rgba(0, 240, 216, 0.12);
  --el-button-hover-border-color: rgba(0, 240, 216, 0.58);
  --el-button-hover-text-color: var(--accent);
  box-shadow: 0 0 18px rgba(0, 240, 216, 0.07);
}

.panel-actions .favorite-action {
  --el-button-bg-color: rgba(157, 92, 255, 0.12);
  --el-button-border-color: rgba(157, 92, 255, 0.42);
  --el-button-text-color: #d4bdff;
  --el-button-hover-bg-color: rgba(157, 92, 255, 0.2);
  --el-button-hover-border-color: rgba(157, 92, 255, 0.72);
  --el-button-hover-text-color: #f0e8ff;
  box-shadow: 0 0 18px rgba(157, 92, 255, 0.1);
}

.panel-actions .guard-action {
  --el-button-bg-color: rgba(34, 184, 255, 0.1);
  --el-button-border-color: rgba(34, 184, 255, 0.4);
  --el-button-text-color: #a9e4ff;
  --el-button-hover-bg-color: rgba(34, 184, 255, 0.17);
  --el-button-hover-border-color: rgba(34, 184, 255, 0.7);
  --el-button-hover-text-color: #d9f5ff;
  box-shadow: 0 0 18px rgba(34, 184, 255, 0.09);
}

.section-title {
  margin: 22px 0 12px;
  font-weight: 700;
}

.message-list {
  max-height: 280px;
  overflow: auto;
  border-top: 1px solid rgba(0, 240, 216, 0.12);
  border-bottom: 1px solid rgba(0, 240, 216, 0.12);
}

.message-item {
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 240, 216, 0.09);
}

.message-item p {
  margin-top: 5px;
  color: var(--muted);
}

.message-input {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  margin-top: 12px;
}

.workspace {
  max-width: 1320px;
  margin: 0 auto;
  padding: 24px;
}

.workspace-head {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: end;
  margin-bottom: 18px;
}

.workspace-head h3 {
  margin-top: 6px;
  font-size: 30px;
  letter-spacing: 0;
}

.tabs button,
.auth-switch button,
.edit-level-btn {
  border: 1px solid var(--line);
  background: rgba(8, 13, 26, 0.72);
  color: var(--muted);
  padding: 10px 14px;
  cursor: pointer;
  transition: border-color 0.3s ease, color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
  border-radius: 8px;
}

.tabs button.active,
.auth-switch button.active,
.tabs button:hover,
.edit-level-btn:hover {
  color: var(--accent);
  background: rgba(0, 240, 216, 0.1);
  border-color: rgba(0, 240, 216, 0.55);
  box-shadow: 0 0 20px rgba(0, 240, 216, 0.12);
}

.filters {
  margin-bottom: 18px;
}

.filters .search-field-select {
  width: 116px;
}

.filters .status-filter-select {
  width: 104px;
}

.filters .el-input {
  width: 320px;
}

.filters .el-select .el-input {
  width: 100%;
}

.level-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(282px, 1fr));
  gap: 16px;
}

.level-card {
  border: 1px solid var(--line);
  background: rgba(11, 16, 30, 0.66);
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.3s ease, border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
  border-radius: 8px;
}

.level-card:hover {
  transform: translateY(-3px);
  border-color: var(--line-strong);
  background: rgba(15, 23, 42, 0.76);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.36), 0 0 24px rgba(0, 240, 216, 0.1);
}

.cover {
  position: relative;
  height: 184px;
  background:
    linear-gradient(rgba(0, 240, 216, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(157, 92, 255, 0.04) 1px, transparent 1px),
    #0b101e;
  background-size: 26px 26px;
}

.cover img,
.empty-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.empty-cover {
  display: grid;
  place-items: center;
  color: var(--accent);
  font-family: "Courier New", monospace;
  text-shadow: 0 0 18px rgba(0, 240, 216, 0.22);
}

.cover::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(5, 9, 20, 0.78), transparent 58%);
}

.cover .status-badge,
.cover .audit-badge {
  position: absolute;
  z-index: 1;
  top: 10px;
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.36);
  background: rgba(5, 9, 20, 0.72);
  padding: 5px 8px;
  font-size: 12px;
  border-radius: 8px;
}

.cover .status-badge {
  right: 10px;
}

.cover .audit-badge {
  left: 10px;
  color: #fde68a;
  border-color: rgba(250, 204, 21, 0.46);
  background: rgba(113, 63, 18, 0.58);
}

.card-body {
  padding: 16px;
}

.card-body h4 {
  margin: 8px 0;
  font-size: 19px;
  letter-spacing: 0;
}

.card-body p {
  min-height: 46px;
  color: var(--muted);
  line-height: 1.55;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.card-owner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 240, 216, 0.09);
}

.card-owner span {
  color: var(--muted);
  font-size: 12px;
}

.card-owner strong {
  color: var(--accent);
  font-size: 13px;
  text-align: right;
  letter-spacing: 0;
  word-break: break-word;
}

.card-owner.vacant strong {
  color: #94a3b8;
}

.card-stats {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 16px;
  color: var(--muted);
  font-size: 12px;
}

.patrol-note {
  margin-top: 12px;
  color: var(--muted);
  font-size: 12px;
  display: grid;
  gap: 6px;
  padding: 9px 10px;
  border: 1px solid rgba(0, 240, 216, 0.14);
  background: rgba(0, 240, 216, 0.045);
  border-radius: 8px;
}

.patrol-note .return-note {
  color: var(--accent);
}

.patrol-note .return-note.is-soon {
  color: #fde68a;
}

.patrol-note .return-note.is-expired {
  color: #fb7185;
}

.edit-level-btn {
  width: 100%;
  margin-top: 12px;
}

.patrol-level-btn {
  color: var(--blue);
  border-color: rgba(34, 184, 255, 0.4);
  background: rgba(34, 184, 255, 0.08);
}

.profile-screen {
  max-width: 1320px;
  margin: 0 auto;
}

.profile-shell {
  min-height: calc(100vh - 120px);
  border: 1px solid var(--line);
  background:
    linear-gradient(180deg, rgba(0, 240, 216, 0.05), rgba(157, 92, 255, 0.024)),
    var(--surface);
  border-radius: 8px;
  padding: 24px;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.38),
    0 0 26px rgba(0, 240, 216, 0.055),
    inset 0 1px 0 rgba(0, 240, 216, 0.1);
  backdrop-filter: blur(26px) saturate(125%);
}

.profile-back {
  display: inline-flex;
  align-items: center;
  min-height: 42px;
  margin-bottom: 22px;
  padding: 0 16px;
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.42);
  background: rgba(0, 240, 216, 0.09);
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 0 22px rgba(0, 240, 216, 0.11);
}

.profile-back::before {
  content: "<";
  margin-right: 8px;
  font-weight: 700;
}

.profile-layout {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(360px, 520px);
  gap: 20px;
  align-items: stretch;
}

.password-layout {
  grid-template-columns: minmax(0, 0.82fr) minmax(360px, 500px);
}

.profile-summary,
.profile-panel {
  border: 1px solid var(--line);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(0, 240, 216, 0.05), rgba(157, 92, 255, 0.024)),
    rgba(8, 13, 26, 0.68);
  padding: 24px;
}

.profile-summary {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 420px;
}

.profile-summary h2 {
  max-width: 720px;
  margin-top: 14px;
  font-size: clamp(38px, 5vw, 68px);
  line-height: 0.98;
}

.profile-summary p {
  max-width: 560px;
  margin-top: 18px;
  color: var(--muted);
  line-height: 1.8;
}

.profile-badge {
  margin-top: 44px;
  border-left: 2px solid var(--accent);
  background: rgba(0, 240, 216, 0.06);
  padding: 16px 18px;
  border-radius: 0 8px 8px 0;
}

.profile-badge strong,
.profile-badge span {
  display: block;
}

.profile-badge strong {
  color: var(--text);
  font-size: 24px;
}

.profile-badge span {
  margin-top: 8px;
  color: var(--muted);
}

.profile-panel {
  align-self: start;
}

.profile-form {
  margin-top: 18px;
}

.profile-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.profile-link-btn {
  --el-button-bg-color: rgba(157, 92, 255, 0.12);
  --el-button-border-color: rgba(157, 92, 255, 0.42);
  --el-button-text-color: #d4bdff;
  --el-button-hover-bg-color: rgba(157, 92, 255, 0.2);
  --el-button-hover-border-color: rgba(157, 92, 255, 0.72);
  --el-button-hover-text-color: #f0e8ff;
}

.editor-screen {
  width: 100%;
  height: calc(100vh - 44px);
  min-height: 720px;
  overflow: hidden;
}

.editor-shell {
  width: 100%;
  max-width: 1680px;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.editor-header,
.editor-block,
.live-preview {
  border: 1px solid var(--line);
  background:
    linear-gradient(180deg, rgba(0, 240, 216, 0.045), rgba(157, 92, 255, 0.022)),
    rgba(11, 16, 30, 0.72);
  border-radius: 8px;
  box-shadow: 0 20px 70px rgba(0, 0, 0, 0.38), 0 0 26px rgba(0, 240, 216, 0.055);
  backdrop-filter: blur(24px) saturate(125%);
}

.editor-header {
  flex: 0 0 auto;
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 18px;
  padding: 20px;
  margin-bottom: 18px;
}

.editor-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  margin-bottom: 14px;
  padding: 0 16px;
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.46);
  background: rgba(0, 240, 216, 0.11);
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 0 24px rgba(0, 240, 216, 0.12);
  transition: border-color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
}

.editor-back::before {
  content: "<";
  font-weight: 700;
}

.editor-back:hover {
  border-color: rgba(0, 240, 216, 0.72);
  background: rgba(0, 240, 216, 0.16);
  box-shadow: 0 0 32px rgba(0, 240, 216, 0.18);
  transform: translateY(-1px);
}

.editor-header h2 {
  margin-top: 8px;
  font-size: clamp(28px, 4vw, 48px);
  line-height: 1;
}

.editor-header-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.editor-workspace {
  flex: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 440px);
  gap: 18px;
  align-items: start;
  width: 100%;
  min-width: 0;
  min-height: 0;
}

.preview-pane,
.editor-panel {
  gap: 14px;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow-x: hidden;
  padding-right: 4px;
  scrollbar-gutter: stable;
}

.preview-pane {
  display: block;
  overflow-y: auto;
}

.editor-panel {
  display: grid;
  overflow-y: auto;
}

.live-preview {
  position: relative;
  height: 100%;
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background-position: center;
  background-size: cover;
  scrollbar-gutter: stable;
}

.live-preview::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    linear-gradient(rgba(0, 240, 216, 0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(157, 92, 255, 0.04) 1px, transparent 1px);
  background-size: 34px 34px;
}

.preview-shade {
  display: none;
}

.preview-content {
  position: relative;
  z-index: 1;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 36px;
}

.preview-content::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    linear-gradient(90deg, rgba(5, 9, 20, 0.92), rgba(5, 9, 20, 0.48)),
    linear-gradient(180deg, rgba(5, 9, 20, 0.08), rgba(5, 9, 20, 0.94));
}

.preview-content > * {
  position: relative;
  z-index: 1;
}

.preview-content h2 {
  margin: 16px 0;
  font-size: clamp(36px, 5vw, 76px);
  line-height: 0.98;
}

.preview-content > p {
  max-width: 720px;
  color: var(--muted);
  line-height: 1.8;
}

.preview-cover-card {
  width: min(520px, 100%);
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  align-items: stretch;
  margin-top: 22px;
  padding: 12px;
  border: 1px solid rgba(0, 240, 216, 0.18);
  border-radius: 8px;
  background: rgba(5, 10, 22, 0.46);
  backdrop-filter: blur(16px);
}

.preview-cover-img {
  height: 150px;
  display: grid;
  place-items: center;
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.2);
  border-radius: 8px;
  background:
    linear-gradient(rgba(0, 240, 216, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(157, 92, 255, 0.035) 1px, transparent 1px),
    #0b101e;
  background-position: center;
  background-size: cover;
}

.preview-cover-card strong {
  display: block;
  margin-top: 6px;
  color: var(--text);
}

.preview-metrics {
  max-width: 720px;
}

.preview-html {
  max-width: 820px;
  max-height: none;
}

.block-head strong {
  display: block;
  color: var(--text);
}

.editor-block {
  padding: 18px;
  min-width: 0;
}

.block-head {
  margin-bottom: 16px;
}

.upload-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.upload-card {
  min-height: 118px;
  text-align: left;
  color: var(--muted);
  border: 1px dashed rgba(0, 240, 216, 0.32);
  background:
    linear-gradient(135deg, rgba(0, 240, 216, 0.06), rgba(157, 92, 255, 0.035)),
    rgba(8, 13, 26, 0.68);
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.3s ease, color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
}

.upload-card:hover {
  color: var(--text);
  border-color: rgba(0, 240, 216, 0.58);
  box-shadow: 0 0 22px rgba(0, 240, 216, 0.1);
}

.upload-card span,
.upload-card small {
  display: block;
}

.upload-card strong {
  display: block;
  margin: 8px 0;
  color: var(--accent);
  font-size: 18px;
}

.file-input {
  display: none;
}

.studio-form {
  display: grid;
  gap: 14px;
}

.dialog-title {
  color: var(--accent);
  font-weight: 700;
  text-shadow: 0 0 14px rgba(0, 240, 216, 0.18);
}

.login-head {
  position: relative;
  padding: 8px 0 4px;
}

.login-head::after {
  content: "";
  display: block;
  width: 100%;
  height: 1px;
  margin-top: 18px;
  background: linear-gradient(90deg, rgba(0, 240, 216, 0.55), rgba(157, 92, 255, 0.1), transparent);
}

.login-head strong {
  display: block;
  margin-top: 8px;
  color: var(--text);
  font-size: 30px;
  line-height: 1;
}

.login-head p {
  max-width: 320px;
  margin-top: 12px;
  color: var(--muted);
  line-height: 1.65;
}

.auth-switch {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 18px;
}

.login-dialog .auth-switch {
  gap: 10px;
  padding: 4px;
  border: 1px solid rgba(0, 240, 216, 0.18);
  background: rgba(4, 9, 20, 0.62);
  border-radius: 8px;
}

.login-dialog .auth-tab {
  min-height: 42px;
  border-color: rgba(0, 240, 216, 0.28);
  font-weight: 800;
  color: #a9e4ff;
}

.login-dialog .auth-tab.active {
  color: #06101d;
  border-color: rgba(0, 240, 216, 0.88);
  background: linear-gradient(135deg, #00f0d8, #22b8ff);
  box-shadow: 0 0 26px rgba(0, 240, 216, 0.3);
}

.full-button {
  width: 100%;
}

.explorer .el-button {
  border-radius: 8px;
}

.explorer .el-button--primary {
  --el-button-bg-color: rgba(0, 240, 216, 0.1);
  --el-button-border-color: rgba(0, 240, 216, 0.58);
  --el-button-text-color: var(--accent);
  --el-button-hover-bg-color: rgba(0, 240, 216, 0.16);
  --el-button-hover-border-color: rgba(34, 184, 255, 0.72);
  --el-button-hover-text-color: #cffff8;
}

.login-overlay {
  background: rgba(2, 5, 12, 0.84) !important;
  backdrop-filter: blur(8px) saturate(120%);
}

.login-dialog.el-dialog,
.login-dialog .el-dialog,
.edit-dialog .el-dialog {
  width: min(92vw, var(--dialog-width, 420px));
  background:
    linear-gradient(145deg, rgba(0, 240, 216, 0.08), rgba(157, 92, 255, 0.045) 46%, rgba(5, 10, 22, 0.18)),
    linear-gradient(180deg, rgba(6, 12, 26, 0.98), rgba(8, 13, 26, 0.98)) !important;
  border: 1px solid rgba(0, 240, 216, 0.34) !important;
  border-radius: 8px;
  box-shadow: 0 30px 120px rgba(0, 0, 0, 0.62), 0 0 44px rgba(0, 240, 216, 0.12) !important;
  backdrop-filter: blur(26px) saturate(125%);
}

.login-dialog.el-dialog {
  --dialog-width: 480px;
}

.login-dialog .el-dialog__header,
.login-dialog.el-dialog .el-dialog__header,
.login-dialog .el-dialog__body,
.login-dialog.el-dialog .el-dialog__body {
  background: transparent !important;
}

.login-dialog .el-dialog__body,
.login-dialog.el-dialog .el-dialog__body {
  padding: 18px 28px 28px !important;
}

.login-dialog .el-dialog__header,
.login-dialog.el-dialog .el-dialog__header {
  padding: 28px 28px 0 !important;
  margin-right: 0 !important;
}

.login-dialog .el-dialog__headerbtn,
.login-dialog.el-dialog .el-dialog__headerbtn {
  top: 18px !important;
  right: 18px !important;
  width: 42px !important;
  height: 42px !important;
  border: 1px solid rgba(255, 107, 122, 0.58);
  background: rgba(255, 107, 122, 0.12);
  border-radius: 8px;
  box-shadow: 0 0 24px rgba(255, 107, 122, 0.14);
  transition: border-color 0.25s ease, background-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}

.login-dialog .el-dialog__headerbtn:hover,
.login-dialog.el-dialog .el-dialog__headerbtn:hover {
  border-color: rgba(255, 107, 122, 0.9);
  background: rgba(255, 107, 122, 0.2);
  box-shadow: 0 0 32px rgba(255, 107, 122, 0.26);
  transform: translateY(-1px);
}

.login-dialog .el-dialog__close,
.login-dialog.el-dialog .el-dialog__close {
  color: #ff8b97 !important;
  font-size: 22px !important;
  font-weight: 900 !important;
}

.edit-dialog .el-dialog {
  --dialog-width: 960px;
}

.login-dialog .el-form-item__label,
.edit-dialog .el-form-item__label,
.profile-form .el-form-item__label {
  color: var(--muted);
}

.edit-form .el-input__wrapper,
.edit-form .el-textarea__inner,
.profile-form .el-input__wrapper {
  border-radius: 8px;
  background: rgba(4, 9, 20, 0.86) !important;
  box-shadow: 0 0 0 1px rgba(0, 240, 216, 0.24) inset !important;
}

.login-dialog .el-input__wrapper {
  border-radius: 8px;
  background: transparent !important;
  box-shadow: 0 0 0 1px rgba(0, 240, 216, 0.32) inset !important;
}

.login-dialog .el-input__inner,
.edit-form .el-input__inner,
.edit-form .el-textarea__inner,
.profile-form .el-input__inner {
  color: var(--text) !important;
}

.login-dialog .el-input__inner {
  background: transparent !important;
  color: #fff !important;
  caret-color: var(--accent);
  -webkit-text-fill-color: #fff !important;
  transition: background-color 9999s ease-out 0s;
}

.login-dialog .el-input__inner:-webkit-autofill,
.login-dialog .el-input__inner:-webkit-autofill:hover,
.login-dialog .el-input__inner:-webkit-autofill:focus {
  -webkit-text-fill-color: #fff !important;
  box-shadow: 0 0 0 1000px rgba(5, 10, 22, 0.92) inset !important;
  caret-color: var(--accent);
}

.edit-form .el-input__count,
.edit-form .el-input__count-inner,
.profile-form .el-input__count,
.profile-form .el-input__count-inner {
  color: #fff !important;
  background: transparent !important;
  box-shadow: none !important;
}

.login-dialog .el-form-item {
  margin-bottom: 20px;
}

.login-dialog .full-button {
  min-height: 50px;
  margin-top: 6px;
  font-weight: 900;
}

.login-dialog .auth-submit-btn {
  --el-button-bg-color: #00f0d8;
  --el-button-border-color: rgba(0, 240, 216, 0.9);
  --el-button-text-color: #06101d;
  --el-button-hover-bg-color: #64fff1;
  --el-button-hover-border-color: #22b8ff;
  --el-button-hover-text-color: #06101d;
  box-shadow: 0 0 28px rgba(0, 240, 216, 0.26), 0 12px 28px rgba(0, 0, 0, 0.28);
}

.login-dialog .el-input__wrapper:hover,
.login-dialog .el-input__wrapper.is-focus {
  box-shadow: 0 0 0 1px rgba(0, 240, 216, 0.56) inset, 0 0 22px rgba(0, 240, 216, 0.12) !important;
}

.edit-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.rich-editor {
  width: 100%;
  min-width: 0;
  min-height: 0;
  border: 1px solid rgba(0, 240, 216, 0.22);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(8, 13, 26, 0.82);
  --w-e-toolbar-bg-color: #08111f;
  --w-e-toolbar-color: var(--text);
  --w-e-toolbar-border-color: rgba(0, 240, 216, 0.18);
  --w-e-toolbar-active-bg-color: rgba(0, 240, 216, 0.12);
  --w-e-toolbar-active-color: var(--accent);
  --w-e-textarea-bg-color: rgba(5, 10, 22, 0.92);
  --w-e-textarea-color: var(--text);
  --w-e-textarea-slight-color: var(--muted);
  --w-e-textarea-slight-bg-color: rgba(0, 240, 216, 0.08);
  --w-e-textarea-border-color: rgba(0, 240, 216, 0.34);
  --w-e-textarea-selected-border-color: var(--accent);
  --w-e-textarea-handler-bg-color: var(--accent);
  --w-e-modal-button-bg-color: rgba(0, 240, 216, 0.08);
  --w-e-modal-button-border-color: rgba(0, 240, 216, 0.28);
}

.rich-editor.w-e-full-screen-container {
  z-index: 5000 !important;
  border: 0;
  border-radius: 0;
  background: #07101d;
  box-shadow: none;
}

.rich-editor.w-e-full-screen-container .editor-body {
  height: auto !important;
  min-height: 0;
  flex: 1 !important;
}

.rich-editor.w-e-full-screen-container .w-e-text-container {
  min-height: 0;
}

.editor-toolbar {
  min-width: 0;
  border-bottom: 1px solid rgba(0, 240, 216, 0.18) !important;
}

.editor-body {
  height: 520px !important;
}

body.level-editor-fullscreen-active {
  overflow: hidden;
}

body.level-editor-fullscreen-active .editor-screen,
body.level-editor-fullscreen-active .editor-shell,
body.level-editor-fullscreen-active .editor-workspace,
body.level-editor-fullscreen-active .editor-panel,
body.level-editor-fullscreen-active .studio-form,
body.level-editor-fullscreen-active .editor-block {
  overflow: visible !important;
  filter: none !important;
  backdrop-filter: none !important;
  transform: none !important;
}

.edit-actions {
  justify-content: flex-end;
}

.compact {
  padding: 14px;
}

.compact .command,
.compact .reader-layout,
.compact .workspace-head {
  grid-template-columns: 1fr;
}

@media (max-width: 980px) {
  .editor-screen,
  .editor-shell {
    height: auto;
    overflow: visible;
  }

  .command,
  .reader-layout,
  .editor-workspace,
  .profile-layout,
  .password-layout {
    grid-template-columns: 1fr;
  }

  .workspace-head,
  .topbar,
  .editor-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .edit-grid,
  .upload-grid,
  .reader-cover {
    grid-template-columns: 1fr;
  }

  .live-preview,
  .preview-content {
    min-height: 560px;
  }

  .preview-pane,
  .editor-panel {
    height: auto;
    max-height: none;
    overflow: visible;
    padding-right: 0;
  }

  .profile-summary {
    min-height: auto;
  }

  .profile-panel {
    align-self: stretch;
  }
}
</style>
